import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PasswordResetModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.message) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    setSending(true);
    try {
      await api.post("/auth/password-reset-request", form);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          message: "",
        });
        onOpenChange(false);
      }, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: "",
      });
      setSubmitted(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="p-0 w-[calc(100vw-1.5rem)] sm:max-w-[520px] max-h-[92vh] overflow-y-auto overflow-x-hidden border-0 shadow-2xl rounded-none sm:rounded-md"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#007EAE] text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h3 className="font-semibold text-lg">Récupération de compte</h3>
          <button
            onClick={handleClose}
            className="text-white/90 hover:text-white p-1"
            aria-label="Fermer"
            disabled={sending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <DialogTitle className="sr-only">Demande de récupération de mot de passe</DialogTitle>
        <DialogDescription className="sr-only">
          Remplissez ce formulaire pour demander la récupération de votre mot de passe.
        </DialogDescription>

        {/* Body */}
        <div className="px-5 sm:px-7 py-6">
          {!submitted ? (
            <>
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 mb-4">
                <AlertCircle className="w-5 h-5 text-[#007EAE] mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-700 leading-relaxed">
                  Remplissez ce formulaire. Votre conseiller vous contactera dans les plus brefs délais pour vous aider à récupérer l'accès à votre compte.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[13px] font-medium text-slate-700">Prénom *</label>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      placeholder="Votre prénom"
                      className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                      disabled={sending}
                    />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-slate-700">Nom *</label>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      placeholder="Votre nom"
                      className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                      disabled={sending}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[13px] font-medium text-slate-700">E-mail *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="votre.email@exemple.com"
                    className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                    disabled={sending}
                  />
                </div>

                <div>
                  <label className="text-[13px] font-medium text-slate-700">Numéro de téléphone *</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                    disabled={sending}
                  />
                </div>

                <div>
                  <label className="text-[13px] font-medium text-slate-700">Votre demande *</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Exemple : J'ai oublié mon mot de passe et je n'arrive plus à me connecter..."
                    rows={4}
                    className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none resize-none"
                    disabled={sending}
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold py-3 btn-press disabled:opacity-70 rounded-none"
                >
                  {sending ? "Envoi en cours…" : "Envoyer la demande"}
                </button>
              </form>
            </>
          ) : (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Demande envoyée !</h3>
              <p className="text-slate-600 text-sm">
                Votre demande a été transmise à nos conseillers. Vous serez contacté très prochainement pour récupérer l'accès à votre compte.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordResetModal;
